import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VitalManualEntryLayout from "../../components/Common/VitalManualEntryLayout";
import {
  SingleVitalInput,
  getVitalInputError,
} from "../../components/Common/VitalInput";
import { submitHealthProfileEntry } from "../../utils/submitHealthProfileEntry";
import { useSubmitVitalForm } from "../../hooks/useSubmitVitalForm";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";

const BREATHING_RATE_MIN = 5;
const BREATHING_RATE_MAX = 60;

export interface BreathingRateManualEntryProps {
  onBack?: () => void;
  onCancel?: () => void;
  /** Called after save succeeds; may be async (e.g. refetch). Metric screen shows loading until this completes. */
  onSave?: () => void | Promise<void>;
}

export default function BreathingRateManualEntry({
  onBack,
  onCancel,
  onSave,
}: BreathingRateManualEntryProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const handleBack = onBack ?? (() => navigate(-1));
  const handleCancel = onCancel ?? (() => navigate(-1));

  const getSubmitPromise = useCallback(
    () => submitHealthProfileEntry("BreathingRate", value),
    [value]
  );

  const hasValidationError = getVitalInputError(
    value,
    BREATHING_RATE_MIN,
    BREATHING_RATE_MAX
  );

  const { handleSave, isSubmitting } = useSubmitVitalForm({
    getSubmitPromise,
    canSubmit: !!value && value !== "-" && !hasValidationError,
    errorMessage: t(
      "Metric.manualEntry.updateError",
      "Unable to save. Please try again."
    ),
    onSuccess: onSave,
  });

  if (isSubmitting) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  return (
    <VitalManualEntryLayout
      title={t("BreathingRate.manualEntry.title")}
      onBack={handleBack}
      onCancel={handleCancel}
      onSave={handleSave}
      isSaveDisabled={
        !value || value === "-" || isSubmitting || hasValidationError
      }
      cancelText={t("BreathingRate.manualEntry.cancel")}
      saveText={t("BreathingRate.manualEntry.save")}
    >
      <SingleVitalInput
        value={value}
        onChange={setValue}
        placeholder="16"
        ariaLabel={t("BreathingRate.manualEntry.inputLabel")}
        unit={t("BreathingRate.unit")}
        min={BREATHING_RATE_MIN}
        max={BREATHING_RATE_MAX}
        integerOnly
        errorText={t(
          "BreathingRate.manualEntry.validationError",
          "Breathing rate must be between 5 and 60 breaths/min"
        )}
      />
    </VitalManualEntryLayout>
  );
}
